import { MID_AIR_SHOTS, UNLIMITED_AMMO, type GameState, type WeaponId } from '@cuboom/shared';
import { drawJumpIcon, drawWeaponIcon } from './icons.js';

/**
 * What the next tap/drag on the field will do.
 * Weapon kinds get slingshot drag-aim; jump is a slingshot too (pull back,
 * hop the other way); swap drags onto an enemy.
 */
export type Selection =
  | { kind: 'jump' }
  | { kind: 'swap' }
  | { kind: 'weapon'; weaponId: WeaponId };

export type ToolbarPick = Selection | { kind: 'pass' };

export interface ToolbarCbs {
  onPick(pick: ToolbarPick): void;
}

interface Spec {
  key: string;
  icon: string;
  name: string;
  pick: ToolbarPick;
  /** Show a live-count badge (ammo / swap charges). */
  badge?: boolean;
}

/**
 * Bottom action toolbar: tappable icons only (no text buttons in play —
 * the mobile spec allows icon taps). Lives in #ui-root, so pointer events
 * never reach the canvas gestures underneath.
 */
export class Toolbar {
  readonly root: HTMLDivElement;
  private readonly els = new Map<string, HTMLButtonElement>();

  constructor(parent: HTMLElement, weapons: WeaponId[], cbs: ToolbarCbs) {
    this.root = document.createElement('div');
    this.root.style.cssText =
      'position:absolute;left:50%;bottom:0;transform:translateX(-50%);' +
      'display:flex;gap:6px;padding:8px 10px calc(8px + env(safe-area-inset-bottom,0px));' +
      'background:rgba(8,12,20,.55);border-radius:14px 14px 0 0;z-index:6;max-width:100vw;overflow-x:auto';

    const specs: Spec[] = [
      { key: 'jump', icon: '🦘', name: 'Jump', pick: { kind: 'jump' } },
      ...weapons.map((w) => ({
        key: w,
        icon: '',
        name: w,
        pick: { kind: 'weapon', weaponId: w } as ToolbarPick,
        badge: true,
      })),
      { key: 'swap', icon: '🔁', name: 'Swap', pick: { kind: 'swap' }, badge: true },
      { key: 'pass', icon: '⏭️', name: 'Pass', pick: { kind: 'pass' } },
    ];

    for (const s of specs) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.title = s.name; // full name on hover when the label ellipsizes
      btn.style.cssText =
        'position:relative;display:flex;flex-direction:column;align-items:center;gap:2px;' +
        'min-width:52px;padding:6px 8px;border:1px solid rgba(255,255,255,.18);border-radius:10px;' +
        'background:rgba(255,255,255,.08);color:#fff;cursor:pointer;font:10px/1.2 system-ui;' +
        'touch-action:manipulation;transition:transform .08s,box-shadow .08s';
      const icon = document.createElement('span');
      icon.style.cssText =
        'font-size:22px;line-height:1;display:flex;align-items:center;justify-content:center;min-height:22px';
      if (s.pick.kind === 'weapon') {
        // Procedural fruit art — the emoji versions lie about color.
        const cv = document.createElement('canvas');
        drawWeaponIcon(cv, s.pick.weaponId);
        icon.appendChild(cv);
      } else if (s.pick.kind === 'jump') {
        // Procedural hop art — 🦘 was a kangaroo, not our sprouts.
        const cv = document.createElement('canvas');
        drawJumpIcon(cv);
        icon.appendChild(cv);
      } else {
        icon.textContent = s.icon;
      }
      const label = document.createElement('span');
      label.textContent = s.name;
      label.style.cssText =
        'text-transform:capitalize;opacity:.85;font-size:9px;max-width:58px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
      btn.append(icon, label);

      if (s.badge) {
        const badge = document.createElement('span');
        badge.style.cssText =
          'position:absolute;top:-6px;right:-4px;min-width:16px;padding:1px 4px;' +
          'border-radius:8px;background:#1c2433;border:1px solid rgba(255,255,255,.35);' +
          'font:700 10px/1.3 system-ui;color:#ffd76a;text-align:center';
        badge.dataset.badge = s.key;
        btn.appendChild(badge);
      }

      // blur so a focused button doesn't swallow space/enter game keys later
      btn.addEventListener('click', () => {
        cbs.onPick(s.pick);
        btn.blur();
      });
      this.els.set(s.key, btn);
      this.root.appendChild(btn);
    }

    parent.appendChild(this.root);
  }

  /**
   * Refresh disabled/selected/badge state from the live sim. `jumpArmed` is
   * the stack's jump slot — it glows alongside the selected weapon, so a
   * "jump + fruit" stack reads as two lit icons at once. `mine` is turn
   * ownership: false during the CPU's turn and during an online opponent's
   * turn, which darkens the whole bar exactly the same way for both.
   * `myTeamId` is whose INVENTORY is on display — ours, always: a playmate's
   * turn dims the bar, it never swaps in their shelf. Null (no seat on this
   * device) falls back to the active team.
   */
  sync(
    state: GameState,
    sel: Selection,
    jumpArmed: boolean,
    mine: boolean,
    myTeamId: number | null,
  ): void {
    const active = state.entities.find((e) => e.id === state.activeEntityId);
    // Private shelf: counts and charges read this device's team, never the
    // active player's — "not my turn" hides the goods behind a dim, it does
    // not show someone else's.
    const team =
      (myTeamId != null ? state.teams.find((t) => t.id === myTeamId) : null) ??
      (active ? state.teams.find((t) => t.id === active.teamId) : null);
    // No point tappable actions at the CPU's turn or the other player's —
    // whoever isn't holding this device (or this seat) drives nothing here.
    const aiming =
      state.phase === 'aiming' && !!active && active.alive && !team?.isCpu && mine;

    const setEnabled = (key: string, on: boolean): void => {
      const b = this.els.get(key);
      if (!b) return;
      b.disabled = !on;
      b.style.opacity = on ? '1' : '0.35';
      b.style.cursor = on ? 'pointer' : 'default';
    };
    const setBadge = (key: string, n: number): void => {
      const b = this.els.get(key)?.querySelector<HTMLElement>('[data-badge]');
      // Tuned ∞ magazines (UNLIMITED_AMMO) read as the symbol, not "999".
      if (b) b.textContent = n >= UNLIMITED_AMMO ? '∞' : String(n);
    };

    setEnabled('jump', aiming && !state.allowance.jump);
    setEnabled('pass', aiming);
    setEnabled('swap', aiming && !state.allowance.swap && (team?.swapLeft ?? 0) > 0);
    setBadge('swap', team?.swapLeft ?? 0);

    // Mid-air: a jump turns the character into a firing platform, so the
    // one-shot-per-turn rule relaxes into MID_AIR_SHOTS while airborne.
    const airborne = !!active && !active.onGround;
    const canShoot =
      aiming && (!state.allowance.fire || (airborne && state.turnInfo.midAirShots < MID_AIR_SHOTS));

    for (const [key, b] of this.els) {
      if (key === 'jump' || key === 'pass' || key === 'swap') {
        continue;
      }
      const weaponId = key as WeaponId;
      const ammo = team ? (team.ammo[weaponId] ?? 0) : 0;
      setEnabled(key, canShoot && ammo > 0);
      setBadge(key, ammo);
    }

    // Selection glow: gold for the armed weapon, blue for movement picks.
    // Jump reads its own stack slot, so jump + weapon can be lit together.
    for (const [key, b] of this.els) {
      const weaponId = key as WeaponId;
      const selected =
        key === 'jump'
          ? jumpArmed
          : key === 'swap'
            ? sel.kind === 'swap'
            : key === 'pass'
              ? false
              : sel.kind === 'weapon' && sel.weaponId === weaponId;
      const hue = key === 'jump' || key === 'swap' ? '126,200,255' : '255,215,106';
      b.style.boxShadow = selected ? `0 0 0 2px rgba(${hue},1), 0 0 12px rgba(${hue},.55)` : '';
      b.style.transform = selected ? 'translateY(-3px)' : '';
      b.style.background = selected ? `rgba(${hue},.16)` : 'rgba(255,255,255,.08)';
    }
  }
}
